// ============================================================
// SideQuests - Skeleton Loaders (Track A)
//
// Lightweight shimmering placeholders for every major surface.
// Design rules:
//   - GPU-accelerated: `.skeleton-shimmer::after` sweeps with
//     `transform: translateX()` only; bars/blocks pulse with
//     `opacity` only. No layout-triggering properties animate.
//   - Zero CLS: every skeleton reserves the EXACT footprint of
//     the content it replaces (fixed heights / min-heights on
//     every variant), so data swap cannot shift the page.
//   - Dark-glass theme: bars derive from the existing
//     `rgba(255,255,255,…)` card tokens - no new hues.
//   - `aria-hidden` + `role="status"`/`aria-busy` region so
//     assistive tech announces loading without reading garbage.
// ============================================================

import React from 'react';

interface SkeletonBaseProps {
  count?: number;
  className?: string;
}

function SkeletonRegion({ children, label = 'Loading' }: { children: React.ReactNode; label?: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className="skeleton-shimmer">
      {children}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

/** Quest feed cards - mirrors JobCard layout/footprint. */
export function QuestFeedSkeleton({ count = 3, className }: SkeletonBaseProps) {
  return (
    <SkeletonRegion label="Loading quests">
      <div className={className} aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="skeleton-quest-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div className="skeleton-bar" style={{ width: '34%', height: 16 }} />
              <div className="skeleton-bar" style={{ width: '18%', height: 22 }} />
            </div>
            <div className="skeleton-bar" style={{ width: '52%', height: 12 }} />
            <div className="skeleton-bar wide" style={{ height: 12 }} />
            <div className="skeleton-block" style={{ height: 34, marginTop: 2 }} />
            <div className="skeleton-bar" style={{ width: '62%', height: 11 }} />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Quest detail - header card + info grid + walkthrough placeholder. */
export function QuestDetailSkeleton() {
  return (
    <SkeletonRegion label="Loading quest details">
      <div aria-hidden="true">
        <div className="skeleton-quest-card" style={{ minHeight: 220 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div className="skeleton-bar" style={{ width: '40%', height: 16 }} />
            <div className="skeleton-bar" style={{ width: '20%', height: 26 }} />
          </div>
          <div className="skeleton-bar" style={{ width: '78%', height: 20 }} />
          <div className="skeleton-bar wide" style={{ height: 12 }} />
          <div className="skeleton-bar" style={{ width: '90%', height: 12 }} />
          <div className="skeleton-bar" style={{ width: '64%', height: 12 }} />
          <div className="skeleton-block" style={{ height: 40, marginTop: 6 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '12px 0' }}>
          <div className="skeleton-card" style={{ height: 72, marginBottom: 0 }}>
            <div className="skeleton-bar" style={{ width: '40%', height: 10 }} />
            <div className="skeleton-bar" style={{ width: '70%', height: 13 }} />
          </div>
          <div className="skeleton-card" style={{ height: 72, marginBottom: 0 }}>
            <div className="skeleton-bar" style={{ width: '40%', height: 10 }} />
            <div className="skeleton-bar" style={{ width: '70%', height: 13 }} />
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** Chat thread - inbound/outbound bubbles with reserved height. */
export function ChatThreadSkeleton() {
  return (
    <SkeletonRegion label="Loading messages">
      <div className="skeleton-chat-history" aria-hidden="true" style={{ minHeight: 'calc(100dvh - 240px)' }}>
        <div className="skeleton-bubble right" />
        <div className="skeleton-bubble" style={{ width: '54%' }} />
        <div className="skeleton-bubble right" style={{ width: '42%' }} />
        <div className="skeleton-bubble" />
        <div className="skeleton-bubble right" />
        <div className="skeleton-bubble" style={{ width: '46%' }} />
      </div>
    </SkeletonRegion>
  );
}

/** Chat list - avatar + two-line rows. */
export function ChatListSkeleton({ count = 5 }: SkeletonBaseProps) {
  return (
    <SkeletonRegion label="Loading conversations">
      <div aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="skeleton-row">
            <div className="skeleton-avatar-sm" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="skeleton-bar" style={{ width: '44%', height: 13 }} />
              <div className="skeleton-bar" style={{ width: '76%', height: 11, marginTop: 6 }} />
            </div>
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Profile - header card + stat grid + section placeholders. */
export function ProfileSkeleton() {
  return (
    <SkeletonRegion label="Loading profile">
      <div aria-hidden="true">
        <div className="skeleton-card" style={{ height: 190, borderRadius: 20 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div className="skeleton-avatar" style={{ width: 74, height: 74 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="skeleton-bar" style={{ width: '55%', height: 18 }} />
              <div className="skeleton-bar" style={{ width: '34%', height: 12, marginTop: 8 }} />
              <div className="skeleton-bar" style={{ width: '70%', height: 12, marginTop: 8 }} />
            </div>
          </div>
          <div className="skeleton-block" style={{ height: 8, borderRadius: 999, marginTop: 16 }} />
          <div className="skeleton-bar" style={{ width: '40%', height: 12, marginTop: 10 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, margin: '14px 0' }}>
          {[0, 1, 2].map((k) => (
            <div key={k} className="skeleton-card" style={{ height: 64, marginBottom: 0, alignItems: 'center', justifyContent: 'center' }}>
              <div className="skeleton-bar" style={{ width: '50%', height: 18 }} />
            </div>
          ))}
        </div>
        {[0, 1, 2].map((k) => (
          <div key={k} className="skeleton-card" style={{ height: 72 }}>
            <div className="skeleton-bar" style={{ width: '36%', height: 14 }} />
            <div className="skeleton-bar" style={{ width: '80%', height: 11 }} />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Community feed rows - icon + name + message. */
export function CommunityFeedSkeleton({ count = 6 }: SkeletonBaseProps) {
  return (
    <SkeletonRegion label="Loading community feed">
      <div aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="skeleton-row">
            <div className="skeleton-avatar-sm" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="skeleton-bar" style={{ width: '30%', height: 12 }} />
              <div className="skeleton-bar" style={{ width: '82%', height: 11, marginTop: 6 }} />
            </div>
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Leaderboard - podium + ranked rows. */
export function LeaderboardSkeleton() {
  return (
    <SkeletonRegion label="Loading leaderboard">
      <div aria-hidden="true">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 18 }}>
          {[0, 1, 2].map((k) => (
            <div key={k} className="skeleton-card" style={{ height: 128, marginBottom: 0, alignItems: 'center' }}>
              <div className="skeleton-bar" style={{ width: 44, height: 44, borderRadius: '50%' }} />
              <div className="skeleton-bar" style={{ width: '70%', height: 12 }} />
              <div className="skeleton-bar" style={{ width: '45%', height: 16 }} />
            </div>
          ))}
        </div>
        {[0, 1, 2, 3, 4].map((k) => (
          <div key={k} className="skeleton-row">
            <div className="skeleton-avatar-sm" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="skeleton-bar" style={{ width: '42%', height: 13 }} />
              <div className="skeleton-bar" style={{ width: '62%', height: 11, marginTop: 6 }} />
            </div>
            <div className="skeleton-bar" style={{ width: 42, height: 16 }} />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Inline page-level fallback for route Suspense (replaces spinner). */
export function PageSkeleton() {
  return (
    <SkeletonRegion label="Loading page">
      <div aria-hidden="true">
        <div className="skeleton-card" style={{ height: 66 }} />
        {[0, 1, 2].map((k) => (
          <div key={k} className="skeleton-quest-card" style={{ minHeight: 140 }}>
            <div className="skeleton-bar" style={{ width: '40%', height: 14 }} />
            <div className="skeleton-bar wide" style={{ height: 12 }} />
            <div className="skeleton-bar" style={{ width: '58%', height: 12 }} />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}
